import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import memberService from '../../services/memberService';
import { useTenant } from '../../contexts/TenantContext';
import {
  Container, Box, Typography, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Avatar, Chip, TextField, InputAdornment, CircularProgress,
  Button, Grid, Alert, Divider, IconButton, Tooltip,
  Select, MenuItem, FormControl, InputLabel, Card, CardContent,
  useTheme, Fade
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  PersonAdd as PersonAddIcon,
  FileUpload as FileUploadIcon,
  GetApp as GetAppIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  PeopleOutline as PeopleIcon,
  ImportContacts as ImportContactsIcon,
  ArrowForward as ArrowForwardIcon,
  PhoneIphone as PhoneIcon,
  Email as EmailIcon,
  SwapHoriz as SwapHorizIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const StaffMemberListPage = () => {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [memberStats, setMemberStats] = useState({
    total: 0,
    active: 0,
    pending: 0,
    expired: 0
  });
  const { tenant } = useTenant();
  const theme = useTheme();

  // Status options for the filter
  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'pending', label: 'Pending' },
    { value: 'expired', label: 'Expired' },
    { value: 'inactive', label: 'Inactive' }
  ];

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date)) return 'Invalid Date';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Format phone number
  const formatPhoneNumber = (phoneNumberString) => {
    if (!phoneNumberString) return 'N/A';
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return phoneNumberString;
  };

  // Format membership number
  const formatMembershipNumber = (number) => {
    if (!number) return 'N/A';
    return number;
  };

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await memberService.getAllMembers();
        console.log("Members data:", data);
        
        // Handle different API response structures
        let membersData = [];
        if (data && data.data) {
          if (Array.isArray(data.data)) {
            membersData = data.data;
          } else if (data.data.members) {
            membersData = data.data.members;
          } else if (data.data.data) {
            membersData = data.data.data;
          }
        } else if (Array.isArray(data)) {
          membersData = data;
        }

        // Ensure we have userId populated
        membersData = membersData.filter(member => member && member.userId);
        
        setMembers(membersData);
        setFilteredMembers(membersData);
        
        // Calculate member stats
        const stats = {
          total: membersData.length,
          active: membersData.filter(m => m.status === 'active').length,
          pending: membersData.filter(m => m.status === 'pending').length,
          expired: membersData.filter(m => m.status === 'expired').length
        };
        setMemberStats(stats);
      } catch (err) {
        console.error("Error fetching members:", err);
        setError('Failed to load members. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (tenant) {
      fetchMembers();
    }
  }, [tenant]);

  // Apply filters when search term or status filter changes
  useEffect(() => {
    if (!members.length) return;
    
    let filtered = [...members];
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(member => member.status === statusFilter);
    }
    
    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(member => 
        (member.userId?.firstName?.toLowerCase().includes(search) || false) ||
        (member.userId?.lastName?.toLowerCase().includes(search) || false) ||
        (member.userId?.email?.toLowerCase().includes(search) || false) ||
        (member.membershipNumber?.toLowerCase().includes(search) || false)
      );
    }
    
    setFilteredMembers(filtered);
    setPage(0); // Reset to first page when filters change
  }, [members, searchTerm, statusFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const refreshMembers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await memberService.getAllMembers();
      
      // Handle different API response structures
      let membersData = [];
      if (data && data.data) {
        if (Array.isArray(data.data)) {
          membersData = data.data;
        } else if (data.data.members) {
          membersData = data.data.members;
        } else if (data.data.data) {
          membersData = data.data.data;
        }
      } else if (Array.isArray(data)) {
        membersData = data;
      }

      // Ensure we have userId populated
      membersData = membersData.filter(member => member && member.userId);
      
      setMembers(membersData);
      
      // Apply current filters
      let filtered = [...membersData];
      if (statusFilter !== 'all') {
        filtered = filtered.filter(member => member.status === statusFilter);
      }
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        filtered = filtered.filter(member => 
          (member.userId?.firstName?.toLowerCase().includes(search) || false) ||
          (member.userId?.lastName?.toLowerCase().includes(search) || false) ||
          (member.userId?.email?.toLowerCase().includes(search) || false) ||
          (member.membershipNumber?.toLowerCase().includes(search) || false)
        );
      }
      setFilteredMembers(filtered);
      
      // Update stats
      const stats = {
        total: membersData.length,
        active: membersData.filter(m => m.status === 'active').length,
        pending: membersData.filter(m => m.status === 'pending').length,
        expired: membersData.filter(m => m.status === 'expired').length
      };
      setMemberStats(stats);
    } catch (err) {
      console.error("Error refreshing members:", err);
      setError('Failed to refresh members. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: 4 }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #5c6bc0 0%, #3949ab 100%)',
          color: 'white',
          position: 'relative'
        }}
      >
        <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
          <PeopleIcon sx={{ mr: 1 }} /> Members
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.8, maxWidth: '800px' }}>
          View all members in your organization. Use the filters to narrow down the list or search for specific members.
        </Typography>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            borderLeft: '4px solid #3f51b5',
            borderRadius: 2,
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-3px)',
              boxShadow: 3
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: '#3f51b520', color: '#3f51b5', mr: 2 }}>
                  <PeopleIcon />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Total Members
                  </Typography>
                  <Typography variant="h5" component="div">
                    {memberStats.total}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            borderLeft: '4px solid #4caf50',
            borderRadius: 2,
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-3px)',
              boxShadow: 3
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: '#4caf5020', color: '#4caf50', mr: 2 }}>
                  <CheckIcon />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Active Members
                  </Typography>
                  <Typography variant="h5" component="div">
                    {memberStats.active}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            borderLeft: '4px solid #ff9800',
            borderRadius: 2,
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-3px)',
              boxShadow: 3
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: '#ff980020', color: '#ff9800', mr: 2 }}>
                  <SwapHorizIcon />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Pending Members
                  </Typography>
                  <Typography variant="h5" component="div">
                    {memberStats.pending}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            borderLeft: '4px solid #f44336',
            borderRadius: 2,
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-3px)',
              boxShadow: 3
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: '#f4433620', color: '#f44336', mr: 2 }}>
                  <ErrorIcon />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Expired Members
                  </Typography>
                  <Typography variant="h5" component="div">
                    {memberStats.expired}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter Section */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by name, email, or membership #"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                label="Status"
              >
                {statusOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={refreshMembers}
              sx={{ borderRadius: 1 }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Display any errors */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Member Table */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : filteredMembers.length === 0 ? (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <ImportContactsIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
            <Typography variant="h6" color="text.secondary">No members found</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {searchTerm || statusFilter !== 'all' 
                ? 'Try changing your search criteria or filters'
                : 'There are no members in the system yet'}
            </Typography>
          </Box>
        ) : (
          <Fade in={!loading}>
            <Box>
              <TableContainer>
                <Table sx={{ minWidth: 650 }} aria-label="members table">
                  <TableHead sx={{ bgcolor: 'rgba(0, 0, 0, 0.04)' }}>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Membership #</TableCell>
                      <TableCell>Contact Information</TableCell>
                      <TableCell>Join Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredMembers
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((member) => {
                        const user = member.userId || {};
                        
                        return (
                          <TableRow key={member._id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar 
                                  sx={{ mr: 2, bgcolor: theme.palette.primary.light }}
                                  src={user.profilePicture}
                                >
                                  {user.firstName?.[0]}{user.lastName?.[0]}
                                </Avatar>
                                <Box>
                                  <Typography variant="body1">
                                    {user.firstName} {user.lastName}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {user.role || 'member'}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {formatMembershipNumber(member.membershipNumber)}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                  <EmailIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary', fontSize: 16 }} />
                                  {user.email || 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                  <PhoneIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary', fontSize: 16 }} />
                                  {formatPhoneNumber(user.phone || member.phone)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {formatDate(member.joinDate)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={member.status?.toUpperCase() || 'UNKNOWN'}
                                size="small"
                                color={
                                  member.status === 'active' ? 'success' :
                                  member.status === 'pending' ? 'warning' :
                                  member.status === 'expired' ? 'error' :
                                  'default'
                                }
                                variant={member.status === 'active' ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="View Details">
                                <IconButton 
                                  component={Link} 
                                  to={`/staff/members/${member._id}`}
                                  size="small"
                                  color="primary"
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredMembers.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          </Fade>
        )}
      </Paper>
    </Container>
  );
};

export default StaffMemberListPage;
